import { useMemo } from 'react';
import { Icon } from '@iconify/react';
import { Button } from '@/components/shadcn/button';
import { Card } from '@/components/shadcn/card';
import type { PadelGameListItem, PadelSessionListItem, PadelStandingItem } from '@/services/padelService';
import type { FlatPadelGame } from './padelListUtils';
import PadelMatchCard from './PadelMatchCard';
import PadelStandingsTable from './PadelStandingsTable';
import PadelStatistikTabs, { type PadelStatistikTab } from './PadelStatistikTabs';
import PadelTeamRecap from './PadelTeamRecap';

interface PadelStatistikContentProps {
  tab: PadelStatistikTab;
  onTabChange: (tab: PadelStatistikTab) => void;
  canEdit: boolean;
  standings: PadelStandingItem[];
  flatGames: FlatPadelGame[];
  sessions: PadelSessionListItem[];
  isSparring: boolean;
  teamAName: string;
  teamBName: string;
  onTambahGame: () => void;
  onReset: () => void;
  onEditScore: (item: FlatPadelGame, gameNumber: number) => void;
  onEditPlayer: (game: PadelGameListItem) => void;
  onDelete: (item: FlatPadelGame, gameNumber: number) => void;
  onGenerateNextRound?: (sessionSlug: string) => void;
  generatingNextRound?: boolean;
  onShare?: () => void;
  onDownload?: () => void;
  downloading?: boolean;
}

export default function PadelStatistikContent({
  tab,
  onTabChange,
  canEdit,
  standings,
  flatGames,
  sessions,
  isSparring,
  teamAName,
  teamBName,
  onTambahGame,
  onReset,
  onEditScore,
  onEditPlayer,
  onDelete,
  onGenerateNextRound,
  generatingNextRound,
  onShare,
  onDownload,
  downloading,
}: PadelStatistikContentProps) {
  const normalizedStandings = useMemo(() => {
    return standings.map((item) => {
      const hasPoints = (item.pointsScored ?? 0) > 0 || (item.pointsConceded ?? 0) > 0;
      return {
        ...item,
        gamesPlayed: hasPoints ? item.gamesPlayed : 0,
        winRate: hasPoints ? item.winRate : 0,
        wins: hasPoints ? item.wins : 0,
        losses: hasPoints ? item.losses : 0,
      };
    });
  }, [standings]);

  const mexicanoSession = sessions.find((s) => s.rotationFormat === 'mexicano');
  let latestRoundNumber = 0;
  let hasIncompleteGameInLatestRound = false;

  if (mexicanoSession && mexicanoSession.games) {
    for (const g of mexicanoSession.games) {
      const r = g.roundNumber ?? 1;
      if (r > latestRoundNumber) {
        latestRoundNumber = r;
      }
    }
    hasIncompleteGameInLatestRound = mexicanoSession.games.some(
      (g) => (g.roundNumber ?? 1) === latestRoundNumber && g.status !== 'completed'
    );
  }

  return (
    <div className="space-y-4">
      <PadelStatistikTabs tab={tab} onTabChange={onTabChange} />

      {tab === 'games' && (
        <div className="space-y-3 pb-24">
          {flatGames.length === 0 ? (
            <Card className="rounded-2xl border-0 bg-white px-4 py-10 text-center text-sm text-gray-400 shadow-[inset_0_0_0_1px_#E0E6ED]">
              Belum ada pertandingan
            </Card>
          ) : (
            flatGames.map((item, index) => (
              <PadelMatchCard
                key={item.game.slug}
                game={item.game}
                sessionType={item.sessionType}
                rotationFormat={item.rotationFormat}
                index={index}
                gameNumber={index + 1}
                canEdit={canEdit}
                isSparring={isSparring}
                teamAName={teamAName}
                teamBName={teamBName}
                onEditScore={() => onEditScore(item, index + 1)}
                onEditLineup={() => onEditPlayer(item.game)}
                onDelete={() => onDelete(item, index + 1)}
              />
            ))
          )}
          {canEdit && mexicanoSession && (
            <div className="space-y-1.5 pt-1">
              <Button
                type="button"
                disabled={hasIncompleteGameInLatestRound || generatingNextRound}
                onClick={() => onGenerateNextRound?.(mexicanoSession.slug)}
                className="h-11 w-full rounded-full bg-[#FC480D] text-sm font-bold text-white shadow-sm hover:bg-[#D83B08] disabled:bg-gray-200 disabled:text-gray-400"
              >
                <Icon icon="mdi:shuffle-variant" className="h-4 w-4" />
                {generatingNextRound
                  ? 'Men-generate Ronde...'
                  : `Generate Ronde ${latestRoundNumber + 1}`}
              </Button>
              {hasIncompleteGameInLatestRound && (
                <p className="text-center text-xs text-gray-400">
                  Selesaikan skor Ronde {latestRoundNumber} untuk generate ronde berikutnya
                </p>
              )}
            </div>
          )}
          {canEdit && flatGames.length > 0 && (
            <Button
              type="button"
              variant="outline"
              onClick={onReset}
              className="h-10 w-full rounded-full border-red-100 text-sm font-semibold text-red-500 hover:bg-red-50"
            >
              <Icon icon="mdi:refresh" className="h-4 w-4" />
              Reset Game
            </Button>
          )}
        </div>
      )}

      {tab === 'standings' && (
        <div className="space-y-3">
          {isSparring && (
            <PadelTeamRecap sessions={sessions} teamAName={teamAName} teamBName={teamBName} />
          )}
          <PadelStandingsTable standings={normalizedStandings} />

          {canEdit && normalizedStandings.length > 0 && (onShare || onDownload) && (
            <div className="flex items-center gap-3 pt-1">
              {onShare && (
                <button
                  type="button"
                  onClick={onShare}
                  className="flex-1 h-11 sm:h-12 rounded-2xl bg-[#FFF5EE] text-[#FC480D] font-bold text-xs sm:text-sm flex items-center justify-center gap-2 border border-[#FFEDD5] hover:bg-[#FFEAE0] active:scale-98 transition-all cursor-pointer"
                >
                  <Icon icon="ph:share-network" className="w-4 h-4 sm:w-5 sm:h-5 text-[#FC480D]" />
                  <span>Bagikan</span>
                </button>
              )}
              {onDownload && (
                <button
                  type="button"
                  onClick={onDownload}
                  disabled={downloading}
                  className="flex-1 h-11 sm:h-12 rounded-2xl bg-[#FC480D] hover:bg-[#D83B08] text-white font-bold text-xs sm:text-sm flex items-center justify-center gap-2 active:scale-98 transition-all shadow-sm cursor-pointer disabled:opacity-60"
                >
                  {downloading ? (
                    <>
                      <Icon icon="ph:spinner-bold" className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                      <span>Mengunduh...</span>
                    </>
                  ) : (
                    <>
                      <Icon icon="ph:download-simple-bold" className="w-4 h-4 sm:w-5 sm:h-5" />
                      <span>Download Statistik</span>
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          <p className="px-1 text-[11px] leading-relaxed text-gray-400">
            M · Main &nbsp; WR · Win rate &nbsp; W-L · Menang-kalah
            <br />
            Poin · Poin dapat-kebobolan
          </p>
        </div>
      )}

      {canEdit && tab === 'games' && !isSparring && (
        <Button
          type="button"
          onClick={onTambahGame}
          className="fixed bottom-[calc(5.5rem+env(safe-area-inset-bottom,0px))] right-4 z-30 h-12 rounded-full bg-[#FC480D] px-5 text-sm font-bold text-white shadow-lg hover:bg-[#D83B08]"
        >
          <Icon icon="mdi:plus" className="h-5 w-5" />
          Tambah Game
        </Button>
      )}
    </div>
  );
}
